import type { SimulationPlatform } from '@lensora/shared'
import { cn } from '@lensora/ui'
import * as React from 'react'

import { formatPlatformName, isGrowthGatedPlatform, PICKER_PLATFORMS } from './PlatformIcon'

// Ring stroke colours — tuned for this widget (mirrors PlatformIcon.tsx's
// brand colours but not identical; the icon-badge colours read poorly as
// thin ring strokes). Typed against every SimulationPlatform so a new
// platform fails to compile here until it's given a colour + radius.
//
// Radii step by 10 (not the original 12) so the innermost ring still clears
// the centre %/label text now that there are 7 rings instead of 5 — the
// centre text block reaches ~16-17px from the SVG centre, so the innermost
// ring's inner edge (r - strokeWidth / 2) needs to stay comfortably above
// that. Outer radius (86) is unchanged to keep the widget's overall size.
const RING_STYLE: Record<SimulationPlatform, { color: string; r: number }> = {
  chatgpt: { color: '#74AA9C', r: 86 },
  claude: { color: '#C96442', r: 76 },
  perplexity: { color: '#20B8CD', r: 66 },
  gemini: { color: '#4285F4', r: 56 },
  'google-ai': { color: '#34A853', r: 46 },
  'chatgpt-search': { color: '#0EA5E9', r: 36 },
  'claude-search': { color: '#F59E0B', r: 26 },
}

const PLATFORMS = PICKER_PLATFORMS.map((key) => ({
  key,
  label: formatPlatformName(key),
  growthOnly: isGrowthGatedPlatform(key),
  ...RING_STYLE[key],
}))

interface PlatformStat {
  mentioned: number
  total: number
}

interface PlatformRingsWidgetProps {
  platformStats: Record<string, PlatformStat>
  isGrowthPlus: boolean
}

export function PlatformRingsWidget({ platformStats, isGrowthPlus }: PlatformRingsWidgetProps) {
  const [hovered, setHovered] = React.useState<string | null>(null)

  const activePlatform = hovered ? PLATFORMS.find((p) => p.key === hovered) : PLATFORMS[0]
  const activeStat = activePlatform ? platformStats[activePlatform.key] : null
  const activePct =
    activeStat && activeStat.total > 0 ? Math.round((activeStat.mentioned / activeStat.total) * 100) : null

  return (
    <div className="rounded-2xl border border-border bg-surface p-4">
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
        AI visibility per model
      </p>
      <p className="mb-3.5 text-[11px] text-text-muted">
        How often each platform mentions your brand
      </p>

      <div className="flex items-center gap-4">
        <svg width="200" height="200" viewBox="0 0 200 200" className="shrink-0">
          {PLATFORMS.map((p) => {
            const stat = platformStats[p.key]
            const pct = stat && stat.total > 0 ? stat.mentioned / stat.total : 0
            const circumference = 2 * Math.PI * p.r
            const filled = pct * circumference
            const isActive = hovered === null || hovered === p.key
            return (
              <g key={p.key} className="transition-opacity duration-200" style={{ opacity: isActive ? 1 : 0.15 }}>
                <circle cx="100" cy="100" r={p.r} fill="none" className="stroke-border" strokeWidth="8" />
                <circle
                  cx="100"
                  cy="100"
                  r={p.r}
                  fill="none"
                  stroke={p.color}
                  strokeWidth="8"
                  strokeDasharray={`${filled} ${circumference}`}
                  strokeDashoffset={circumference * 0.25}
                  strokeLinecap="round"
                />
              </g>
            )
          })}
          <text x="100" y="96" textAnchor="middle" fontSize="17" fontWeight="600" className="fill-text">
            {activePct !== null ? `${activePct}%` : '—'}
          </text>
          <text x="100" y="114" textAnchor="middle" fontSize="11" className="fill-text-muted">
            {activePlatform?.label ?? ''}
          </text>
        </svg>

        <div className="flex flex-1 flex-col gap-1.5">
          {PLATFORMS.map((p) => {
            const stat = platformStats[p.key]
            const pct = stat && stat.total > 0 ? Math.round((stat.mentioned / stat.total) * 100) : null
            const locked = p.growthOnly && !isGrowthPlus
            return (
              <div
                key={p.key}
                onMouseEnter={() => setHovered(p.key)}
                onMouseLeave={() => setHovered(null)}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 transition-colors',
                  hovered === p.key && 'bg-surface-overlay',
                  locked && 'opacity-50'
                )}
              >
                <div className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: p.color }} />
                <span className="flex-1 text-xs text-text">{p.label}</span>
                {locked ? (
                  <span className="text-[10px] text-brand-accent">Growth+</span>
                ) : (
                  <span
                    className={cn('text-xs font-semibold', pct === null && 'text-text-muted')}
                    style={pct !== null ? { color: p.color } : undefined}
                  >
                    {pct !== null ? `${pct}%` : '—'}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
